import { addresses, stakingAbi, tokenAbi } from '../../utils/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import BigNumber from 'bignumber.js';
import {
  Container,
  Text,
  VStack,
  useToast,
  Link,
  Input,
  HStack,
  Button,
  Box,
  InputGroup,
  InputRightElement,
  Center,
} from '@chakra-ui/react';
import StakingActions from './StakingActions';
import { maxUint256 } from 'viem';
import Card from '../../components/Card';
import XEDToken from '../../components/XEDToken';
import Leaderboard from './Leaderboard';

/*
userInfo(address _userAddress)
{
  uint256 stakingStart
  uint256 amount
  bool isEarlyStaker
}
*/

export default function Staking() {
  const { address } = useAccount();

  const toast = useToast();

  const {
    data: userInfo,
    refetch,
    isLoading: isStakeInfoLoading,
    isError: isStakeInfoError,
  } = useReadContract({
    address: addresses.staking,
    abi: stakingAbi,
    functionName: 'userInfo',
    args: address ? [address] : undefined,
    query: {
      enabled: Boolean(address),
    },
  });

  const [stakeAmount, setStakeAmount] = useState('50000');

  const [stakingStart = BigInt(0), amount = BigInt(0)] =
    (userInfo as [bigint, bigint, boolean] | undefined) ?? [];

  const isStaking = useMemo(() => amount > 0, [amount]);

  const { data: allowance = 0 } = useReadContract({
    address: addresses.token,
    abi: tokenAbi,
    functionName: 'allowance',
    args: address ? [address, addresses.staking] : undefined,
    query: {
      enabled: Boolean(address),
    },
  });

  const { data: xedBalance = 0 } = useReadContract({
    address: addresses.token,
    abi: tokenAbi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: Boolean(address),
    },
  });

  const xedBalanceNumber = xedBalance
    ? new BigNumber(xedBalance.toString()).div(10 ** 18).toString()
    : '0';

  // useWriteContract for approve
  const { writeContractAsync, isPending, data: hash } = useWriteContract();

  const {
    isLoading: isConfirming,
    isSuccess: isConfirmed,
    isError,
  } = useWaitForTransactionReceipt({
    hash,
  });

  useEffect(() => {
    if (isConfirmed || isError) {
      refetch();
    }
  }, [isConfirmed, refetch, isError]);

  useEffect(() => {
    if (!address) {
      return;
    }

    const interval = setInterval(() => {
      refetch();
    }, 60000);

    return () => clearInterval(interval);
  }, [address, refetch]);

  const handleApprove = useCallback(async () => {
    try {
      await writeContractAsync({
        address: addresses.token,
        abi: tokenAbi,
        functionName: 'approve',
        args: [addresses.staking, maxUint256],
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Could not approve',
        description: err.message?.split('.')?.[0] ?? err.message,
        status: 'error',
      });
    }
  }, [writeContractAsync, toast]);

  const handleStake = useCallback(async () => {
    try {
      await writeContractAsync({
        address: addresses.staking,
        abi: stakingAbi,
        functionName: 'stake',
        args: [
          BigInt(
            new BigNumber(stakeAmount)
              .times(10 ** 18)
              .integerValue()
              .toString(10),
          ),
        ],
      });
    } catch (err) {
      console.log('err', err.message);
      toast({
        title: 'Could not stake',
        description: err.message?.split('.')?.[0] ?? err.message,
        status: 'error',
      });
      console.error(err);
    }
  }, [toast, writeContractAsync, stakeAmount]);

  const handleUnstake = useCallback(async () => {
    try {
      await writeContractAsync({
        address: addresses.staking,
        abi: stakingAbi,
        functionName: 'unstake',
        args: [],
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Could not unstake',
        description: err.message?.split('.')?.[0] ?? err.message,
        status: 'error',
      });
    }
  }, [toast, writeContractAsync]);

  return (
    <Container pt="0" pb="20" maxW="600px" px="20px">
      <VStack align="start" w="100%">
        <Card>
          <VStack w="100%" align="start">
            <VStack spacing="0.5" align="start" pb="2">
              <Text fontSize="md" fontWeight="semibold">
                $XED Staking
              </Text>
              <Text fontSize="sm" color="whiteAlpha.700" fontWeight="light">
                Stake $XED and participate in the leaderboard for rewards.
              </Text>
            </VStack>
            <Box w="100%" py="0.5" />

            <VStack w="100%" spacing="4">
              {address && isStakeInfoLoading ? (
                <VStack align="start" w="100%" spacing="1.5">
                  <Text fontWeight="light" fontSize="sm" color="whiteAlpha.800">
                    Loading your staking position...
                  </Text>
                </VStack>
              ) : address && isStakeInfoError ? (
                <VStack align="start" w="100%" spacing="2">
                  <Text fontWeight="light" fontSize="sm" color="whiteAlpha.800">
                    Could not load your staking position.
                  </Text>
                  <Button size="sm" onClick={() => refetch()}>
                    Retry
                  </Button>
                </VStack>
              ) : !stakingStart ? (
                <VStack align="start" w="100%" spacing="1.5">
                  <HStack w="100%" justify="space-between">
                    <Text fontWeight="light" fontSize="sm" color="whiteAlpha.800">
                      Stake Amount
                    </Text>
                  </HStack>
                  <HStack w="100%" spacing="2">
                    <InputGroup>
                      <Input
                        variant="filled"
                        value={stakeAmount}
                        onChange={(e) => setStakeAmount(e.target.value)}
                      />
                      <InputRightElement w="60px">
                        <Button
                          variant="ghost"
                          borderLeftRadius="0"
                          borderRightRadius="md"
                          px="5"
                          py="5"
                          color="brand.500"
                          size="xs"
                          _hover={{}}
                          onClick={() => setStakeAmount(xedBalanceNumber)}
                        >
                          Max
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    <XEDToken />
                  </HStack>
                  <HStack spacing="2" fontSize="sm" color="whiteAlpha.800" fontWeight="light">
                    <Text>Balance: {xedBalanceNumber} XED</Text>
                  </HStack>
                </VStack>
              ) : (
                <VStack align="start" w="100%" spacing="0">
                  <Text fontWeight="light" fontSize="sm" color="whiteAlpha.800">
                    Your Stake
                  </Text>
                  <Text fontWeight="semibold">
                    {BigNumber((amount as bigint).toString())
                      .div(10 ** 18)
                      .toString()}{' '}
                    XED
                  </Text>
                </VStack>
              )}
              {(!address || (!isStakeInfoLoading && !isStakeInfoError)) && (
                <StakingActions
                  stake={handleStake}
                  unstake={handleUnstake}
                  isStaking={isStaking}
                  balance={xedBalanceNumber}
                  needsApproval={
                    new BigNumber((allowance as bigint).toString()).isLessThan(
                      new BigNumber(stakeAmount).times(10 ** 18).toString(),
                    ) || !stakeAmount
                  }
                  approve={handleApprove}
                  isLoading={isPending || isConfirming}
                />
              )}
            </VStack>
          </VStack>
        </Card>
      </VStack>
      {hash && (
        <Center w="100%">
          <Text
            w="100%"
            p="3"
            bg="blackAlpha.400"
            borderRadius="lg"
            textAlign="center"
            fontSize="sm"
            as={Link}
            textDecoration="underline"
            href={`https://etherscan.io/tx/${hash}`}
            target="_blank"
          >
            View transaction on Etherscan: {hash?.slice(0, 12)}...{hash?.slice(-12)}
          </Text>
        </Center>
      )}
      <Leaderboard />
    </Container>
  );
}
